/* eslint-disable no-underscore-dangle */
/**
 * Incident-log timeline helpers shared by mock builders and the live walkthrough.
 */

/** Sample clip window used to space untimed bookmarks (~2 min). */
export const SAMPLE_CLIP_DURATION_SEC = 110;

/**
 * Space N section bookmarks across a sample clip.
 * First mark comes sooner (~15–20s); remaining sections spread to the end.
 */
export function spreadPauseBeats(count, { startSeconds = 0, durationSeconds = SAMPLE_CLIP_DURATION_SEC } = {}) {
  const n = Math.max(Number(count) || 1, 1);
  const window = Math.max(durationSeconds - startSeconds, n * 8);
  const firstGap = Math.min(20, Math.max(12, Math.floor(window / (n + 1))));
  const first = startSeconds + firstGap;
  if (n === 1) return [Math.round(Math.min(durationSeconds, Math.max(first, startSeconds + 8)))];

  const span = Math.max(durationSeconds - first, (n - 1) * 8);
  return Array.from({ length: n }, (_, index) => Math.round(first + (span * index) / (n - 1)));
}

/**
 * Coerce API/mock bookmark offsets to a finite number of seconds.
 * Accepts common alternate field names and numeric strings.
 */
export function resolveVideoAtSeconds(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const raw = entry.videoAtSeconds ?? entry.atSeconds ?? entry.seconds ?? entry.offsetSeconds ?? entry.videoSeconds;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Map incident-log rows onto the video timeline as section bookmarks.
 * Timed rows keep their offsets; unmarked rows are spread across the sample clip.
 */
export function alignLogToVideo(incidentLog = [], { startSeconds = 0, pauseAtSeconds } = {}) {
  const log = Array.isArray(incidentLog) ? incidentLog : [];
  if (!log.length) {
    return { incidentLog: log, pauseAtSeconds: Number.isFinite(pauseAtSeconds) ? pauseAtSeconds : undefined };
  }

  const end =
    Number.isFinite(pauseAtSeconds) && pauseAtSeconds > startSeconds
      ? pauseAtSeconds
      : startSeconds + SAMPLE_CLIP_DURATION_SEC;
  const autoMarks = spreadPauseBeats(log.length, { startSeconds, durationSeconds: end });

  const stamped = log.map((entry, index) => {
    const existing = resolveVideoAtSeconds(entry);
    const videoAtSeconds = existing != null ? existing : autoMarks[index];
    return { ...entry, videoAtSeconds };
  });

  const last = stamped[stamped.length - 1];
  return {
    incidentLog: stamped,
    pauseAtSeconds: Number.isFinite(last?.videoAtSeconds) ? last.videoAtSeconds : end,
  };
}

/** Timed incident-log section bookmarks in playback order. */
export function getIncidentBeats(video) {
  const log = Array.isArray(video?.incidentLog) ? video.incidentLog.slice() : [];
  const timed = log.filter((entry) => resolveVideoAtSeconds(entry) != null);
  const source = timed.length ? timed : log;
  return source
    .map((entry) => {
      const videoAtSeconds = resolveVideoAtSeconds(entry);
      return videoAtSeconds == null ? entry : { ...entry, videoAtSeconds };
    })
    .sort((a, b) => (a.videoAtSeconds || 0) - (b.videoAtSeconds || 0));
}

/**
 * Active log row for a playback position (latest bookmark at or before current time).
 * When several rows share the same timestamp, keep the first in log order unless
 * `preferredEntryId` points at another row in that same time bucket (e.g. user jump).
 */
export function resolveActiveIncidentLogEntry(incidentLog = [], currentSeconds = 0, preferredEntryId) {
  const timed = (incidentLog || [])
    .map((entry, index) => {
      const videoAtSeconds = resolveVideoAtSeconds(entry);
      return videoAtSeconds == null ? null : { ...entry, videoAtSeconds, _index: index };
    })
    .filter(Boolean)
    .sort((a, b) => a.videoAtSeconds - b.videoAtSeconds || a._index - b._index);

  if (!timed.length) return incidentLog.find((entry) => entry.isCurrent) || null;

  let active = timed[0];
  timed.forEach((entry) => {
    // Strictly later marks only — equal times keep the first log-order row.
    if (entry.videoAtSeconds <= currentSeconds + 0.35 && entry.videoAtSeconds > active.videoAtSeconds) {
      active = entry;
    }
  });

  if (preferredEntryId != null) {
    const preferred = timed.find((entry) => String(entry.id) === String(preferredEntryId));
    if (
      preferred &&
      Math.abs(preferred.videoAtSeconds - active.videoAtSeconds) <= 0.35 &&
      preferred.videoAtSeconds <= currentSeconds + 0.35
    ) {
      active = preferred;
    }
  }

  const { _index, ...entry } = active;
  return entry;
}
